import React, { useContext, useRef } from 'react';
import AppContext from '../../AppContext';
import { useInputKeyboardHandler } from '../../hooks/useInputKeyboardHandler';

/**
 * Text input component with automatic keyboard handling for mobile devices
 *
 * Features:
 * - Auto-scrolls into view when focused (prevents keyboard covering input)
 * - Theme-aware styling from AppContext
 * - Supports all standard input props
 *
 * @param {Object} props - Standard input props (type, value, onChange, placeholder, etc.)
 * @param {Object} props.style - Additional styles to merge with defaults
 * @param {string} props.type - Input type (text, email, password, time, etc.)
 * @param {boolean} props.autoFocus - Auto-focus on mount
 * @param {Function} props.onFocus - Additional onFocus handler
 * @param {Object} props.keyboardOptions - Options for keyboard handler
 */
const KeyboardAwareInput = ({
  style = {},
  type = 'text',
  onFocus,
  keyboardOptions = {},
  ...props
}) => {
  const { theme } = useContext(AppContext);
  const { inputProps } = useInputKeyboardHandler(keyboardOptions);
  const inputRef = useRef(null);

  // Merge onFocus handlers
  const handleFocus = (e) => {
    if (inputProps.onFocus) inputProps.onFocus(e);
    if (onFocus) onFocus(e);
  };

  // Default theme-aware styles
  const defaultStyles = {
    padding: theme?.spacing?.small || '0.5rem',
    fontSize: theme?.typography?.body || '1rem',
    border: `1px solid ${theme?.colors?.border || '#ccc'}`,
    borderRadius: theme?.spacing?.borderRadius || '4px',
    backgroundColor: theme?.colors?.surface || '#fff',
    color: theme?.colors?.text || '#000',
    outline: 'none',
    width: '100%',
    ...style
  };

  return (
    <input
      ref={inputRef}
      type={type}
      style={defaultStyles}
      onFocus={handleFocus}
      {...props}
    />
  );
};

export default KeyboardAwareInput;
